import React, { useEffect, useState } from "react";
import { X, Download, Printer, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

/**
 * In-app PDF viewer modal.
 *
 * Strategy: fetch PDF as authenticated blob (no token in URL), then render via
 * a single `<iframe src={blobUrl}>`. Earlier versions used `<object>` wrapping
 * `<iframe>` plus `?t=<jwt>` URL — both children fetched the URL and one
 * aborted the other (`net::ERR_ABORTED`), leaving the viewer blank in Chrome.
 *
 * Props:
 *  - open: boolean
 *  - path: API path like "/reports/board" (preferred — auth via Bearer)
 *  - filename: download filename
 *  - onClose: () => void
 *  - url:  legacy fallback (for callers still passing a built URL)
 */
export default function PdfViewerModal({ open, path, url: legacyUrl, filename, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    let revoke = null;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        let bUrl;
        if (path) {
          const r = await api.get(path, { responseType: "blob" });
          bUrl = URL.createObjectURL(r.data);
        } else if (legacyUrl) {
          // Legacy callers still pass a fully-built URL (with ?t= token).
          // Fetch via window.fetch to get bytes and convert to blob.
          const resp = await fetch(legacyUrl, { credentials: "include" });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          bUrl = URL.createObjectURL(blob);
        } else {
          throw new Error("No PDF source provided");
        }
        setBlobUrl(bUrl);
        revoke = bUrl;
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Failed to load PDF");
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
      setBlobUrl("");
    };
  }, [open, path, legacyUrl]);

  if (!open) return null;

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const printPdf = () => {
    const frame = document.getElementById("dara-pdf-frame");
    try {
      frame?.contentWindow?.focus();
      frame?.contentWindow?.print();
    } catch {
      if (blobUrl) window.open(blobUrl, "_blank");
    }
  };

  const openInTab = () => {
    if (blobUrl) window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 bg-black/95 z-[9999] flex flex-col"
      data-testid="pdf-viewer-modal"
    >
      <header className="flex items-center justify-between px-6 py-3 border-b border-zinc-900 bg-black">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000]">// PDF PREVIEW</span>
          <span className="font-mono text-[11px] text-zinc-400 truncate">{filename}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={download}
            disabled={!blobUrl}
            data-testid="pdf-download-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-[#E59E00] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Download className="w-3.5 h-3.5" /> DOWNLOAD
          </button>
          <button
            onClick={openInTab}
            disabled={!blobUrl}
            data-testid="pdf-open-tab-btn"
            className="border border-zinc-700 text-white font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-zinc-900 disabled:opacity-50 flex items-center gap-2"
            title="Open the PDF in a new browser tab"
          >
            <ExternalLink className="w-3.5 h-3.5" /> NEW TAB
          </button>
          <button
            onClick={printPdf}
            disabled={!blobUrl}
            data-testid="pdf-print-btn"
            className="border border-zinc-700 text-white font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-zinc-900 disabled:opacity-50 flex items-center gap-2"
          >
            <Printer className="w-3.5 h-3.5" /> PRINT
          </button>
          <button
            onClick={onClose}
            data-testid="pdf-close-btn"
            className="text-zinc-500 hover:text-white p-2"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </header>
      <div className="flex-1 w-full bg-zinc-800 relative">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-400 font-mono text-[11px] uppercase tracking-widest">
            <Loader2 className="w-5 h-5 animate-spin text-[#FFB000]" />
            Loading {filename}…
          </div>
        )}
        {error && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF3B30]">
              // PDF LOAD FAILED
            </div>
            <div className="font-mono text-[11px] text-zinc-300 max-w-md">{error}</div>
            <button
              onClick={onClose}
              className="border border-zinc-700 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900 mt-2"
            >
              Close
            </button>
          </div>
        )}
        {blobUrl && !error && (
          <iframe
            id="dara-pdf-frame"
            src={blobUrl}
            title={filename}
            className="w-full h-full bg-zinc-800 border-0"
            data-testid="pdf-iframe"
          />
        )}
      </div>
    </div>
  );
}
